import { i18nLocale } from '@weavix/models/src/translate/translate';
import { Point, Vertices } from './location.model';
import { FacilitySettings, WaltSettings } from '@weavix/models/src/facility/facility';
import { Taggable } from '@weavix/models/src/core/core';
import { Walt } from '@weavix/models/src/walt/walt';
import { Channel } from '@weavix/models/src/channel/channel';
import { PendingPerson, Person } from '@weavix/models/src/person/person';
import { ImportedPerson } from './person-imported.model';
import { AnyItem } from '@weavix/models/src/item/item';
import { Dashboard } from './dashboard.model';
import { WorkForm } from '@weavix/models/src/work-forms/work-forms';

export const BEACON_NUMBER_PREFIX = 'weavix-';
export const BEACON_NUMBER_PREFIX_DISPLAY = 'BEACON ';
export const DEFAULT_BEACON_ENTER_DISTANCE = 5;
export const DEFAULT_BEACON_ENTER_TIME = 15_000;
export const DEFAULT_BEACON_EXIT_DISTANCE = 5;
export const DEFAULT_BEACON_EXIT_TIME = 15_000;

export type MapDeviceType = Beacon | Wrangler | WifiRouter;

export interface Beacon {
    id: string;
    facilityId: string;
    structureId?: string;
    location: Point;
    name: string;
    level: number;
    surveyed?: boolean;
    tracking?: boolean;
    event?: boolean;
    battery?: number;
    enterDistance?: number[];
    enterTime?: number;
    exitDistance?: number[];
    exitTime?: number;
    lastSeen?: string;
    snapDistance?: number;
}

export interface TrackingCircle {
    latitude: number;
    longitude: number;
    radius: number;
}

export enum WifiRouterUsage {
    Static = 'static',
    Dynamic = 'dynamic',
    Ignored = 'ignored',
}

export interface WifiRouter {
    id: string;
    ssid: string;
    location: Point;
    level?: number;
    structureId?: string;
    lastSeen: string;
    usage: WifiRouterUsage;
    name?: string;
    height?: number;
    power?: number;
    snapDistance?: number;
}

export interface UpdateWifiRouter {
    id: string;
    usage?: string;
    location?: number[];
    level?: number;
    structureId?: string;
    name?: string;
    ssid?: string;
    height?: number;
    power?: number;
    snapDistance?: number;
}

export interface AddWifiRouter {
    id: string;
    facilityId: string;
    ssid: string;
    location: Point;
    usage: WifiRouterUsage;
    lastSeen: string;
}

export interface Wrangler {
    id: string;
    facilityId: string;
    name: string;
    location: Point;
    structureId?: string;
    level?: number;
    /**
     * Indicates whether this Wrangler belongs to a group or not.
     * If true, the Wrangler is a group and the name is the group name.
     * If false, the Wrangler is an individual.
     * If not set, the Wrangler is an individual.
     * This value will not be saved in the database, but rather user for UI purposes.
     * @type {boolean}
     * @optional
     */
    isGroup?: boolean;
}

export interface UpdateWrangler {
    id: {
        facilityId: string;
        wranglerId: string;
    };
    name: string;
    location: Point;
    structureId?: string;
    level?: number;
}

export interface AddFacilityPersonRequest {
    personId: string;
    facilityId: string;
    group: string;
}

export interface Facility extends Omit<Taggable, 'facilityId'> {
    id?: string;
    accountId?: string;
    name: string;
    address: string;
    location: Point;
    bounds: Vertices;
    zoom: number;
    image?: FacilityImage;
    timezone?: string;
    settings?: FacilitySettings;
    beacons?: Beacon[];
    wranglers?: Wrangler[];
    trackingCircles?: TrackingCircle[];
    waltSettings?: WaltSettings;
    locale?: i18nLocale;
}

export interface FacilityImage {
    uri?: string;
    placement?: {
        bounds: Vertices;
        rotation: number;
        vertices: Vertices;
    };
    opacity?: number;
}

export type UpdateSettingsRequest = Partial<FacilitySettings>;

export interface Simulator {
    personCount?: number;
    itemCount?: number;
    running?: boolean;
    error?: string;
    enabled?: Date;
    disabled?: Date;
}


export const addBeaconNumberPrefix = (id: string): string => {
    const beaconId = id?.replace(new RegExp(BEACON_NUMBER_PREFIX, 'i'), '');
    return `${BEACON_NUMBER_PREFIX}${beaconId}`;
};

export const removeBeaconNumberPrefix = (beacon: Beacon): Beacon => {
    const beaconId = beacon.id?.replace(new RegExp(BEACON_NUMBER_PREFIX, 'i'), '');
    return Object.assign({}, beacon, { id: beaconId });
};

export interface FacilityReferences {
    walts?: Walt[];
    channels?: Channel[];
    people?: Person[];
    pendingPeople?: PendingPerson[];
    importedPeople?: ImportedPerson[];
    items?: AnyItem[];
    dashboards?: Dashboard[];
    workForms?: WorkForm[];
}
